import { useCallback, useEffect, useState } from 'react';
import { isPWAInstalled } from '../utils/deviceDetection';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Capture beforeinstallprompt for an Install button.
 * iOS has no prompt API — callers should show Share → Add to Home Screen copy.
 */
export function useInstallPrompt(): {
  canPrompt: boolean;
  isStandalone: boolean;
  isIos: boolean;
  promptInstall: () => Promise<void>;
} {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [isStandalone, setStandalone] = useState(() =>
    typeof window !== 'undefined' ? isPWAInstalled() : false,
  );

  useEffect(() => {
    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    const mq = window.matchMedia('(display-mode: standalone)');
    const onMode = () => setStandalone(isPWAInstalled());
    mq.addEventListener('change', onMode);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      mq.removeEventListener('change', onMode);
    };
  }, []);

  const isIos =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const promptInstall = useCallback(async () => {
    if (!deferred) return;
    await deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* ignore */
    }
    setDeferred(null);
  }, [deferred]);

  return {
    canPrompt: Boolean(deferred) && !isStandalone,
    isStandalone,
    isIos: isIos && !isStandalone,
    promptInstall,
  };
}
